import React, {
	memo,
	Suspense,
	useCallback,
	useDeferredValue,
	useEffect,
	useMemo,
	useRef,
	useState,
	type FC,
} from 'react';
import debounce from 'lodash/debounce';
import { useLazyLoadQuery } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';

import { useDocChangeContext } from '@confluence/editor-features/entry-points/useDocChangeContext.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';
import { type FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';
import { decodeCollabDraftDocument } from '@confluence/adf-analysis-core/entry-points/parseCollabDraftAdf.ts';
import {
	CollabDraftQuery,
	type CollabDraftQuery$data,
	type CollabDraftQuery$variables,
} from '@confluence/fabric-providers/entry-points/CollabDraftQuery.ts';
import { getDraftShareIdFromQueryParam } from '@confluence/route-manager-utils/entry-points/getDraftShareIdFromQueryParam.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { ReadTimeQuery } from './hooks/useReadTime.tsx';
import type {
	useReadTimeQuery$data,
	useReadTimeQuery$variables,
} from './hooks/__generated__/useReadTimeQuery.graphql.ts';
import { ReadTimePopupWrapper } from './ReadTimePopupWrapper.tsx';
import {
	getAdf,
	getAdfString,
	getContentFeatureExtractedEventObject,
	readTimeFromFeatures,
} from './readTimeUtils.ts';
import { extractAllContentFeatures, type ContentFeatures } from './featuresExtractor.ts';

type EditorReadTimeProps = {
	contentId: string;
	contentType?: string | null;
	editorApi?: FullPageEditorPresetAPI | null;
	showWordCount?: boolean;
	isDetailsPanel?: boolean;
	isAbbreviatedReadTime?: boolean;
};

type EditorReadTimeComponentProps = Omit<EditorReadTimeProps, 'contentType'> & {
	adf: JSONDocNode | null;
	adfString: string | null;
	contentType: string;
	loading: boolean;
	error: ReturnType<typeof useQueryNoFurtherUpdateInterop>['error'];
};

const EditorReadTimeComponent: FC<EditorReadTimeComponentProps> = memo(
	({
		contentId,
		editorApi,
		showWordCount,
		isDetailsPanel,
		isAbbreviatedReadTime = true,
		adf,
		adfString,
		contentType,
		loading,
		error,
	}: EditorReadTimeComponentProps) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { subscribeDocChange, unsubscribeDocChange } = useDocChangeContext();

		// Read time + word count derived from the query ADF in a SINGLE pass (the
		// query data carries the raw ADF string, so its exact length is passed
		// through). Memoized so it only walks when the query ADF actually changes.
		// Features are NOT derived here — they are owned by the live editor
		// document (see below).
		const queryReadTime = useMemo((): {
			readTime: number;
			wordCount: number | null;
			isReadTimeCapped: boolean;
		} => {
			if (!adf) {
				return { readTime: 0, wordCount: 0, isReadTimeCapped: false };
			}

			const { featuresSkipExpand, nodeCount } = extractAllContentFeatures(adf, adfString?.length);
			const { readTime, isReadTimeCapped } = readTimeFromFeatures(featuresSkipExpand, nodeCount);
			return {
				readTime,
				wordCount: isReadTimeCapped ? null : featuresSkipExpand.wordCount,
				isReadTimeCapped,
			};
		}, [adf, adfString]);

		// Initialize with preloaded query data for SSR.
		const [readTime, setReadTime] = useState<number | null>(queryReadTime.readTime);
		const [wordCount, setWordCount] = useState<number | null>(queryReadTime.wordCount);
		const [isReadTimeCapped, setIsReadTimeCapped] = useState(queryReadTime.isReadTimeCapped);
		const [features, setFeatures] = useState<ContentFeatures | null>(null);
		const [featuresSkipExpand, setFeaturesSkipExpand] = useState<ContentFeatures | null>(null);
		const lastAdfStringRef = useRef(adfString);

		useEffect(() => {
			if (!createAnalyticsEvent || !adf || loading || error || !contentId) {
				return;
			}
			createAnalyticsEvent(
				getContentFeatureExtractedEventObject({ contentType, contentId, adf, isInEditor: true }),
			).fire();
		}, [contentId, loading, adf, contentType, error, createAnalyticsEvent]);

		useEffect(() => {
			// Recompute read time from the query ADF only when it actually changed.
			// Features stay owned by the live editor document.
			if (adf && !loading && !error && adfString !== lastAdfStringRef.current) {
				lastAdfStringRef.current = adfString;
				setReadTime(queryReadTime.readTime);
				setWordCount(queryReadTime.wordCount);
				setIsReadTimeCapped(queryReadTime.isReadTimeCapped);
			}
		}, [adf, adfString, loading, error, queryReadTime]);

		const setReadTimeFromEditorState = useCallback(async () => {
			if (editorApi) {
				editorApi.core?.actions.requestDocument((doc) => {
					if (!doc) {
						setReadTime(0);
						setWordCount(0);
						setIsReadTimeCapped(false);
					} else {
						// Single pass over the live editor doc (no serialized string, so no
						// rawLength). Node count, measured during the walk, drives the capped
						// "{floor}+ min read" lower bound.
						const {
							features: allFeatures,
							featuresSkipExpand: allFeaturesSkipExpand,
							nodeCount,
						} = extractAllContentFeatures(doc);
						const { readTime: editorReadTime, isReadTimeCapped: editorCapped } =
							readTimeFromFeatures(allFeaturesSkipExpand, nodeCount);
						setReadTime(editorReadTime);
						setIsReadTimeCapped(editorCapped);
						if (editorCapped) {
							// Walk stopped early — counts are partial, treat as unavailable.
							setWordCount(null);
							setFeatures(null);
							setFeaturesSkipExpand(null);
						} else {
							setWordCount(allFeaturesSkipExpand.wordCount);
							setFeatures(allFeatures);
							setFeaturesSkipExpand(allFeaturesSkipExpand);
						}
					}
				});
			}
		}, [editorApi]);

		useEffect(() => {
			// Sometimes the ADF from the query is outdated if the byline is re-rendered quickly after a document change
			// So recalculate read time from the editor ADF when the editor is ready
			void setReadTimeFromEditorState();
		}, [setReadTimeFromEditorState]);

		const debouncedCalculateReadTime = useMemo(
			() => debounce(setReadTimeFromEditorState, 5000),
			[setReadTimeFromEditorState],
		);

		useEffect(() => {
			subscribeDocChange(debouncedCalculateReadTime);
			return () => unsubscribeDocChange(debouncedCalculateReadTime);
		}, [debouncedCalculateReadTime, subscribeDocChange, unsubscribeDocChange]);

		if (error) {
			return <ErrorDisplay error={error} />;
		}
		if (readTime || (showWordCount && wordCount != null)) {
			return (
				<ReadTimePopupWrapper
					contentId={contentId}
					isAbbreviatedReadTime={isAbbreviatedReadTime}
					isDetailsPanel={isDetailsPanel}
					showWordCount={showWordCount}
					features={features}
					featuresSkipExpand={featuresSkipExpand}
					wordCount={wordCount}
					readTime={readTime}
					source="editorReadTime"
					isReadTimeCapped={isReadTimeCapped}
				/>
			);
		}
	},
);

const EditorReadTimeFromReadTimeQuery: FC<EditorReadTimeProps> = ({
	contentType: _contentType,
	...props
}) => {
	const { contentId } = props;

	const readTimeQueryOptions = {
		variables: {
			contentId,
			status: ['current', 'draft'],
		},
	};

	const { data, loading, error } = useQueryNoFurtherUpdateInterop<
		useReadTimeQuery$data,
		useReadTimeQuery$variables
	>(ReadTimeQuery, readTimeQueryOptions);

	const adf = useMemo(() => getAdf(data ?? undefined, contentId), [data, contentId]);
	const adfString = useMemo(() => getAdfString(data ?? undefined), [data]);

	return (
		<EditorReadTimeComponent
			{...props}
			adf={adf}
			adfString={adfString}
			contentType={data?.content?.nodes?.[0]?.type ?? ''}
			loading={loading}
			error={error}
		/>
	);
};

type CollabDraftQueryOperation = {
	response: CollabDraftQuery$data;
	variables: CollabDraftQuery$variables;
};

const EditorReadTimeWithCollabDraft: FC<EditorReadTimeProps & { draftContentId: string }> = ({
	contentType,
	draftContentId,
	...props
}) => {
	const draftShareId = getDraftShareIdFromQueryParam();

	// Same variables as the editor's CollabDraftQuery, so the store record it loads is reused.
	const data = useLazyLoadQuery<CollabDraftQueryOperation>(
		CollabDraftQuery,
		{
			id: draftContentId,
			hydrateAdf: true,
			...(draftShareId ? { draftShareId } : undefined),
		},
		{ fetchPolicy: 'store-or-network' },
	);

	const adfString = data?.collabDraft?.document ?? null;
	const adf = useMemo(
		() => decodeCollabDraftDocument(adfString) as JSONDocNode | null,
		[adfString],
	);

	return (
		<EditorReadTimeComponent
			{...props}
			adf={adf}
			adfString={adfString}
			contentType={contentType ?? ''}
			// The previous document's draft is shown until the next one loads, as the ReadTimeQuery path does.
			loading={draftContentId !== props.contentId}
			error={undefined}
		/>
	);
};

// Like ErrorDisplay on a ReadTimeQuery error: reports the error and renders nothing.
const RenderNothingOnError = () => null;

const EditorReadTimeFromCollabDraft: FC<EditorReadTimeProps> = (props) => {
	// On a live doc transition the next draft loads without suspending the shown read time.
	const draftContentId = useDeferredValue(props.contentId);

	// The ReadTimeQuery path while its query loads: no document yet, the editor document still counts.
	const loadingReadTime = (
		<EditorReadTimeComponent
			{...props}
			adf={null}
			adfString={null}
			contentType={props.contentType ?? ''}
			loading
			error={undefined}
		/>
	);

	// CollabDraftQuery requires an id.
	if (!draftContentId) {
		return loadingReadTime;
	}

	return (
		<Suspense fallback={loadingReadTime}>
			{/* One boundary per document: its error clears with the next document, as a new ReadTimeQuery did. */}
			<GenericErrorBoundary
				key={draftContentId}
				attribution={Attribution.INHERIT}
				renderOnError={RenderNothingOnError}
			>
				<EditorReadTimeWithCollabDraft {...props} draftContentId={draftContentId} />
			</GenericErrorBoundary>
		</Suspense>
	);
};

/**
 * Read Time component with support for automatically recalculating and updating read time after a document change in the editor.
 */
export const EditorReadTime: FC<EditorReadTimeProps> = memo((props: EditorReadTimeProps) =>
	expValEquals('confluence_editor_content_unified_query', 'isEnabled', true) ? (
		<EditorReadTimeFromCollabDraft {...props} />
	) : (
		<EditorReadTimeFromReadTimeQuery {...props} />
	),
);
